import React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { getParentElement } from "@impero-com/common/utils/dom";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever, clone } from "@impero-com/common/utils/obj";
import { Button, Svg } from "@impero-com/ui";
import RemoveIcon from "@impero-com/ui/img/remove.svg";
import { Checkbox, Table } from "antd";
import { isEqual } from "lodash-es";
import memoizeOne from "memoize-one";

import { UserId, UserOverview, StandardAccessRight } from "api/generated/model";
import { UserTypeahead } from "components/userTypeahead";
import { formatAccessRight } from "utils/accessRights";
import { ResourceAccessRights, UserAndRights } from "utils/accessRights/models";

import * as theme from "./theme.css";

type BulkAccessRightsProps<RAR extends ResourceAccessRights> =
    | ({ type: "CREATE" } & WrappedWriteProps<RAR>)
    | ({ type: "EDIT" } & WrappedWriteProps<RAR>)
    | ({ type: "READ_ONLY" } & WrappedReadProps<RAR>);

type Wrapped<T> = T & { label: string };

interface BulkAccessRightsValues<RAR extends ResourceAccessRights> {
    userAndRights: Array<Array<UserAndRights<RAR>>>;
    absentUsers?: Array<UserOverview>;
}

interface ReadProps<RAR extends ResourceAccessRights> {
    resourceAccessRights: RAR;
    values?: BulkAccessRightsValues<RAR>;
}

type WrappedReadProps<RAR extends ResourceAccessRights> = Wrapped<
    ReadProps<RAR>
>;

interface WriteProps<RAR extends ResourceAccessRights> extends ReadProps<RAR> {
    onChangeUserAccessRight: (
        user: UserOverview,
        accessRight: StandardAccessRight
    ) => void;
    onRemoveUserAccessRight: (userId: UserId) => void;
    onRemoveUser: (userId: UserId) => void;
}

type WrappedWriteProps<RAR extends ResourceAccessRights> = Wrapped<
    WriteProps<RAR>
>;

class BulkAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<BulkAccessRightsProps<RAR>> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { label, resourceAccessRights, values } = this.props;
                return (
                    <WrappedReadOnlyBulkAccessRights
                        label={label}
                        resourceAccessRights={resourceAccessRights}
                        values={values}
                    />
                );
            }
            case "EDIT": {
                const {
                    label,
                    resourceAccessRights,
                    values,
                    onChangeUserAccessRight,
                    onRemoveUserAccessRight,
                    onRemoveUser,
                } = this.props;
                return (
                    <WrappedEditBulkAccessRights
                        label={label}
                        resourceAccessRights={resourceAccessRights}
                        values={values}
                        onChangeUserAccessRight={onChangeUserAccessRight}
                        onRemoveUserAccessRight={onRemoveUserAccessRight}
                        onRemoveUser={onRemoveUser}
                    />
                );
            }
            case "CREATE": {
                const {
                    label,
                    resourceAccessRights,
                    values,
                    onChangeUserAccessRight,
                    onRemoveUserAccessRight,
                    onRemoveUser,
                } = this.props;
                return (
                    <WrappedCreateBulkAccessRights
                        label={label}
                        resourceAccessRights={resourceAccessRights}
                        values={values}
                        onChangeUserAccessRight={onChangeUserAccessRight}
                        onRemoveUserAccessRight={onRemoveUserAccessRight}
                        onRemoveUser={onRemoveUser}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };
}

interface UserAndRightRow {
    user: UserOverview;
    accessRightCounters: Record<StandardAccessRight, number>;
}

type RawBulkAccessRightsProps<RAR extends ResourceAccessRights> =
    | ({
          type: "EDIT";
          onChangeUserAccessRight: (
              user: UserOverview,
              accessRight: StandardAccessRight
          ) => void;
          onRemoveUserAccessRight: (userId: UserId) => void;
          onRemoveUser: (userId: UserId) => void;
      } & ReadProps<RAR>)
    | ({
          type: "READ_ONLY";
      } & ReadProps<RAR>);

interface RawBulkAccessRightsState {
    accessRightForNewUser: StandardAccessRight;
}

class RawBulkAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<
    RawBulkAccessRightsProps<RAR>,
    RawBulkAccessRightsState
> {
    constructor(props: RawBulkAccessRightsProps<RAR>) {
        super(props);
        this.state = {
            accessRightForNewUser:
                props.resourceAccessRights.defaultAccessRight,
        };
    }

    private getUserMap = memoizeOne(
        (
            userAndRights: Array<Array<UserAndRights<RAR>>>,
            absentUsers: Array<UserOverview> | undefined
        ): Record<UserId, UserOverview> => {
            const userMap: Record<UserId, UserOverview> = {};

            for (const uars of userAndRights) {
                const seen = new Set<UserId>();
                for (const uar of uars) {
                    if (seen.has(uar.user.id)) {
                        throw new Error(
                            `User must appear only once, seen ${uar.user.id} twice`
                        );
                    }
                    seen.add(uar.user.id);

                    const prev = userMap[uar.user.id];
                    if (prev !== undefined) {
                        if (!isEqual(uar.user, prev)) {
                            console.error(
                                "A user with same id has different data",
                                prev,
                                uar.user
                            );
                            throw new Error(
                                "A user with same id has different data"
                            );
                        }
                    } else {
                        userMap[uar.user.id] = uar.user;
                    }
                }
            }

            if (absentUsers) {
                for (const user of absentUsers) {
                    const prev = userMap[user.id];
                    if (prev !== undefined) {
                        throw new Error(
                            `Absent user ${user.id} is actually present`
                        );
                    }
                    userMap[user.id] = user;
                }
            }
            return userMap;
        }
    );

    private getUserAndRightCounters = memoizeOne(
        (
            userAndRights: Array<Array<UserAndRights<RAR>>>,
            absentUsers: Array<UserOverview> | undefined
        ): Record<UserId, Record<StandardAccessRight, number>> => {
            const counters: Record<
                UserId,
                Record<StandardAccessRight, number>
            > = {};
            const defaultRecord: Record<StandardAccessRight, number> = {
                Admin: 0,
                Edit: 0,
                View: 0,
            };
            if (absentUsers !== undefined) {
                for (const user of absentUsers) {
                    counters[user.id] = defaultRecord;
                }
            }
            for (const uars of userAndRights) {
                for (const uar of uars) {
                    let counter = counters[uar.user.id];
                    if (counter !== undefined) {
                        counter[uar.accessRight]++;
                    } else {
                        counter = clone(defaultRecord);
                        counter[uar.accessRight] = 1;
                        counters[uar.user.id] = counter;
                    }
                }
            }

            return counters;
        }
    );

    private getTableDataSource = memoizeOne(
        (
            counters: Record<UserId, Record<StandardAccessRight, number>>,
            userMap: Record<UserId, UserOverview>
        ): Array<UserAndRightRow> => {
            const dataSource: Array<UserAndRightRow> = [];
            Object.entries(counters).forEach(([userIdStr, counter]) => {
                const userId = Number(userIdStr);
                dataSource.push({
                    user: userMap[userId],
                    accessRightCounters: counter,
                });
            });
            dataSource.sort(
                Cmp.map((row) => formatUserName(row.user), Cmp.string())
            );
            return dataSource;
        }
    );

    render = () => {
        const { resourceAccessRights, values = { userAndRights: [] } } =
            this.props;
        const { userAndRights, absentUsers } = values;

        const userMap = this.getUserMap(userAndRights, absentUsers);
        const counters = this.getUserAndRightCounters(
            userAndRights,
            absentUsers
        );
        const dataSource = this.getTableDataSource(counters, userMap);

        const columns = [];
        columns.push({
            key: "user",
            className: theme.header,
            title: t("AccessRights_UserColum"),
            render: (text: string, row: UserAndRightRow) => (
                <div>
                    <div>{formatUserName(row.user)}</div>
                    <div className={theme.emailOverflow}>
                        <div className={theme.email}>{row.user.email}</div>
                    </div>
                </div>
            ),
        });
        let summary;
        switch (this.props.type) {
            case "EDIT":
                for (const ar of resourceAccessRights.accessRights) {
                    columns.push({
                        key: `access-right-${ar}`,
                        className: theme.header,
                        width: "5%",
                        title: formatAccessRight(ar),
                        render: (_text: string, row: UserAndRightRow) => {
                            const counter = row.accessRightCounters[ar];
                            const checked = counter === userAndRights.length;
                            const indeterminate =
                                counter > 0 && counter < userAndRights.length;
                            return (
                                <div className={theme.checkbox}>
                                    <Checkbox
                                        checked={checked}
                                        indeterminate={indeterminate}
                                        onClick={() => {
                                            if (this.props.type !== "EDIT") {
                                                throw new Error(
                                                    "expected type EDIT, got " +
                                                        this.props.type
                                                );
                                            }
                                            if (!checked || indeterminate) {
                                                this.props.onChangeUserAccessRight(
                                                    row.user,
                                                    ar
                                                );
                                            } else {
                                                this.props.onRemoveUserAccessRight(
                                                    row.user.id
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            );
                        },
                    });
                }
                columns.push({
                    key: "remove",
                    className: theme.header,
                    width: "5%",
                    render: (_text: string, row: UserAndRightRow) => (
                        <Button
                            id={"remove-button-" + row.user.id.toString()}
                            variant="link"
                            className={theme.removeButton}
                            onClick={() => {
                                if (this.props.type !== "EDIT") {
                                    throw new Error(
                                        "expected type EDIT, got " +
                                            this.props.type
                                    );
                                }
                                this.props.onRemoveUser(row.user.id);
                            }}
                        >
                            <Svg
                                className={theme.removeIcon}
                                src={RemoveIcon}
                            />
                        </Button>
                    ),
                });
                summary = this.footerToAddUser(
                    new Set(
                        Object.keys(userMap).map((userIdStr) =>
                            Number(userIdStr)
                        )
                    ),
                    resourceAccessRights
                );
                break;
            case "READ_ONLY":
                for (const ar of resourceAccessRights.accessRights) {
                    columns.push({
                        key: `access-right-${ar}`,
                        className: theme.header,
                        width: "5%",
                        title: formatAccessRight(ar),
                        render: (_text: string, row: UserAndRightRow) => {
                            const counter = row.accessRightCounters[ar];
                            const checked = counter === userAndRights.length;
                            const indeterminate =
                                counter > 0 && counter < userAndRights.length;
                            return (
                                <div className={theme.checkbox}>
                                    <Checkbox
                                        checked={checked}
                                        indeterminate={indeterminate}
                                        disabled
                                    />
                                </div>
                            );
                        },
                    });
                }
                summary = undefined;
                break;
            default:
                return assertNever(this.props);
        }

        return (
            <div>
                <Table
                    className={theme.table}
                    columns={columns}
                    dataSource={dataSource}
                    rowKey={(row) => row.user.id.toString()}
                    summary={summary}
                    bordered
                    showHeader={true}
                    pagination={false}
                />
            </div>
        );
    };

    private footerToAddUser = (
        userIdsExcludedAutoCompletion: Set<UserId>,
        resourceAccessRights: RAR
    ) => {
        const { accessRightForNewUser } = this.state;

        return () => {
            return (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                        <UserTypeahead
                            id="access-rights-table"
                            value={undefined}
                            onSelect={(user) => {
                                if (this.props.type !== "EDIT") {
                                    throw new Error(
                                        "expected type EDIT, got " +
                                            this.props.type
                                    );
                                }
                                this.props.onChangeUserAccessRight(
                                    user,
                                    accessRightForNewUser
                                );
                            }}
                            placeholder={t("AccessRights_AddNew")}
                            filter={(user) =>
                                !userIdsExcludedAutoCompletion.has(user.id)
                            }
                            getPopupContainer={getParentElement}
                        />
                    </Table.Summary.Cell>
                    {resourceAccessRights.accessRights.map((ar, index) => (
                        <Table.Summary.Cell index={index + 1} key={ar}>
                            <div className={theme.checkbox}>
                                <Checkbox
                                    checked={accessRightForNewUser === ar}
                                    onClick={(checked) => {
                                        if (checked) {
                                            this.setState({
                                                accessRightForNewUser: ar,
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell
                        index={resourceAccessRights.accessRights.length + 1}
                    />
                </Table.Summary.Row>
            );
        };
    };
}

class WrappedReadOnlyBulkAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<WrappedReadProps<RAR>> {
    render = () => {
        const { label, resourceAccessRights, values } = this.props;
        return (
            <div>
                <div className={theme.selectorLabel}>{label}</div>
                <RawBulkAccessRights
                    type="READ_ONLY"
                    resourceAccessRights={resourceAccessRights}
                    values={values}
                />
            </div>
        );
    };
}

class WrappedEditBulkAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<WrappedWriteProps<RAR>> {
    render = () => {
        const {
            label,
            resourceAccessRights,
            values,
            onChangeUserAccessRight,
            onRemoveUserAccessRight,
            onRemoveUser,
        } = this.props;
        return (
            <div>
                <div className={theme.selectorLabel}>{label}</div>
                <RawBulkAccessRights
                    type="EDIT"
                    resourceAccessRights={resourceAccessRights}
                    values={values}
                    onChangeUserAccessRight={onChangeUserAccessRight}
                    onRemoveUserAccessRight={onRemoveUserAccessRight}
                    onRemoveUser={onRemoveUser}
                />
            </div>
        );
    };
}

class WrappedCreateBulkAccessRights<
    RAR extends ResourceAccessRights,
> extends React.PureComponent<WrappedWriteProps<RAR>> {
    render = () => {
        const {
            label,
            resourceAccessRights,
            values,
            onChangeUserAccessRight,
            onRemoveUserAccessRight,
            onRemoveUser,
        } = this.props;
        return (
            <div>
                <div className={theme.selectorLabel}>{label}</div>
                <RawBulkAccessRights
                    type="EDIT"
                    resourceAccessRights={resourceAccessRights}
                    values={values}
                    onChangeUserAccessRight={onChangeUserAccessRight}
                    onRemoveUserAccessRight={onRemoveUserAccessRight}
                    onRemoveUser={onRemoveUser}
                />
            </div>
        );
    };
}

export {
    BulkAccessRights,
    type BulkAccessRightsProps,
    type BulkAccessRightsValues,
};
